import React, { useEffect, useState, useRef, useCallback } from "react";
import { Button, InputGroup, FormControl } from "react-bootstrap";
import { Icon } from "@iconify/react";
import debounce from 'lodash.debounce';

/**
 * QuantitySelector - A controlled component for selecting quantity values
 *
 * Uses debounced callbacks to prevent race conditions from rapid button clicks.
 * UI updates immediately (optimistic), but callbacks are debounced by 600ms.
 *
 * @param {number} quantity - Current quantity value (required)
 * @param {function} onQuantityChange - Callback when quantity changes (debounced for +/- buttons)
 * @param {boolean} showLabel - Whether to show "Qty." label (default: false)
 * @param {boolean} disabled - Whether to disable the quantity selector (default: false)
 */
const QuantitySelector = ({
  quantity,
  onQuantityChange,
  showLabel = false,
  disabled = false,
  inputRef: externalInputRef, // Allow parent to access input ref
}) => {
  const [inputQuantity, setInputQuantity] = useState(quantity);
  const inputRef = useRef(null);
  const isFocusedRef = useRef(false);
  // Track the pending quantity for debounced calls
  const pendingQuantityRef = useRef(quantity);
  
  // Expose input ref to parent if provided
  React.useEffect(() => {
    if (externalInputRef) {
      externalInputRef.current = inputRef.current;
    }
  }, [externalInputRef]);

  // Debounced callback for +/- button clicks
  // This ensures rapid clicks result in a single API call with the final value
  const debouncedButtonCallback = useCallback(
    debounce(() => {
      const newQty = pendingQuantityRef.current;
      if (onQuantityChange) {
        onQuantityChange(newQty);
      }
    }, 600),
    [onQuantityChange]
  );

  // Sync with prop when it changes (after API call completes)
  // But only if input is not focused (user is not actively typing)
  useEffect(() => {
    if (!isFocusedRef.current) {
      setInputQuantity(quantity);
      pendingQuantityRef.current = quantity;
    }
  }, [quantity]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedButtonCallback.cancel();
    };
  }, [debouncedButtonCallback]);

  const handleInputChange = (e) => {
    const value = e.target.value;

    // If empty, allow it temporarily (user might be clearing to type new value)
    if (value === '') {
      setInputQuantity('');
      return;
    }

    // Only allow numeric input
    if (/^\d+$/.test(value)) {
      setInputQuantity(value);
    }
  };

  const handleBlurOrEnter = () => {
    // On blur/enter, ensure we have a valid value
    const parsed = parseInt(inputQuantity, 10);

    if (isNaN(parsed) || parsed < 1) {
      setInputQuantity(1);
      pendingQuantityRef.current = 1;
      if (onQuantityChange) {
        onQuantityChange(1);
      }
    } else {
      pendingQuantityRef.current = parsed;
      if (onQuantityChange) {
        onQuantityChange(parsed);
      }
    }
  };

  const handleIncrementClick = () => {
    const newQty = (parseInt(inputQuantity, 10) || 0) + 1;
    setInputQuantity(newQty);
    pendingQuantityRef.current = newQty;
    debouncedButtonCallback();
  };

  const handleDecrementClick = () => {
    const currentQty = parseInt(inputQuantity, 10) || 1;
    const newQty = Math.max(1, currentQty - 1);
    setInputQuantity(newQty);
    pendingQuantityRef.current = newQty;
    debouncedButtonCallback();
  };

  const content = (
    <InputGroup>
      <Button variant={null} onClick={handleDecrementClick} disabled={disabled}>
        <Icon icon="majesticons:minus-line" />
      </Button>
      <FormControl
        ref={inputRef}
        value={inputQuantity}
        style={{
          textAlign: "center",
          boxShadow: "none",
          outline: "none",
          position: "relative",
          zIndex: 1,
        }}
        onChange={handleInputChange}
        onFocus={() => {
          isFocusedRef.current = true;
        }}
        onBlur={() => {
          isFocusedRef.current = false;
          handleBlurOrEnter();
        }}
        onKeyDown={(e) => {
          // Allow special keys (backspace, delete, arrow keys, tab, etc.)
          const allowedKeys = [
            'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
            'Tab', 'Home', 'End', 'Escape'
          ];

          // Trigger onQuantityChange when Enter is pressed
          // Just blur the input - the onBlur handler will call handleBlurOrEnter
          if (e.key === 'Enter') {
            e.target.blur();
            return;
          }

          // Allow if it's a special key or a digit
          if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
            e.preventDefault();
          }
        }}
        min={1}
        disabled={disabled}
      />
      <Button variant={null} onClick={handleIncrementClick} disabled={disabled}>
        <Icon icon="ic:round-plus" />
      </Button>
    </InputGroup>
  );

  // If showLabel is true, wrap with label (for customer portal)
  if (showLabel) {
    return (
      <div className="d-flex">
        <b className="mt-1">Qty.&nbsp;</b>
        {content}
      </div>
    );
  }

  return content;
};

export default QuantitySelector;
